import * as Yup from "yup";

import orderBy from "lodash/orderBy";
import { UnreachableCaseError } from "ts-essentials";
import { type Schedule, ScheduleType } from "src/components/schedule/types";

import type {
  FullSyncConfig,
  JourneyNode,
  JourneyNodeDetails,
  SegmentPriorityList,
  SplitBranch,
} from "src/pages/journeys/types";
import type { JourneyEventType } from "src/pages/journeys/navigation/types";
import {
  isSegmentBranchNodeDetails,
  isSplitBranchNodeDetails,
} from "src/pages/journeys/utils/type-guards";
import type {
  SegmentBranchConfig,
  SplitBranchConfig,
} from "src/types/journeys";
import {
  type AndOrCondition,
  ConditionType,
  type PropertyCondition,
  IntervalUnit,
} from "src/types/visual";
import { QueryType } from "src/types/models";
import {
  MINUTES_IN_DAY,
  MINUTES_IN_HOUR,
  MINUTES_IN_MONTH,
  MINUTES_IN_WEEK,
  MINUTES_IN_YEAR,
} from "src/pages/journeys/constants";
import type {
  JourneySyncQuery,
  SyncableColumnFragment as SyncableColumn,
  SyncableColumnsQuery,
} from "src/graphql";
import { mergeSyncConfigWithTemplateAndOverrides } from "src/utils/sync-templates";

/**
 * Ensure that the property conditions are wrapped in a top level AND condition
 */
export const getFormattedSubconditions = (
  subcondition: AndOrCondition<PropertyCondition>,
): AndOrCondition<PropertyCondition> => {
  // If top level condition is an AND or OR condition, return
  if (
    subcondition.type === ConditionType.And ||
    subcondition.type === ConditionType.Or
  ) {
    return subcondition;
  }

  return {
    type: ConditionType.And,
    conditions: [subcondition],
  };
};

export const getOrderedEditableSegments = (
  nodes: JourneyNode[],
): SegmentPriorityList => {
  const nodeDetails = nodes.map(({ data }) => data);

  const segmentBranchNodes: JourneyNodeDetails<SegmentBranchConfig>[] =
    nodeDetails.filter(isSegmentBranchNodeDetails);

  const branchNodes = orderBy(
    segmentBranchNodes
      // Catch all node is omitted
      .filter((node) => !node.config.segment_is_catch_all),
    ["config.segment_priority_rank"],
    ["asc"],
  );

  return branchNodes.map((node) => ({
    id: node.id,
    name: node.name,
  }));
};

export const getSplitGroups = (nodes: JourneyNode[]): SplitBranch[] => {
  const nodeDetails = nodes.map(({ data }) => data);

  const splitBranchNodes: JourneyNodeDetails<SplitBranchConfig>[] =
    nodeDetails.filter(isSplitBranchNodeDetails);

  const branchNodes = orderBy(splitBranchNodes, ["asc"]);

  return branchNodes.map((node) => ({
    id: node.id,
    name: node.name,
    percentage: node.config.percentage,
  }));
};

/**
 * Collapses the external-facing event type to the types we use internally.
 **/
export const mapJourneyEventForResolver = (
  event: JourneyEventType,
): { direction?: string; event?: JourneyEventType } => {
  switch (event) {
    case "in-progress":
      return { direction: "in" };
    case "completed-journey":
      return { event: "exited-journey" };
    case "advanced-from":
      return { direction: "from", event: "moved-to-node" };
    case "entered-tile":
      return { direction: "to", event: "moved-to-node" };
    default:
      return { event };
  }
};

export type JourneyInterval = {
  unit: IntervalUnit;
  quantity: number;
};

// Converts ScheduleInterval to a comparable number in minutes.
// This function is a heuristic for month/year, as it doesn't account for non-30day months
// and leap years. The functionality here currently mirrors the existing functionality
// in https://github.com/hightouchio/hightouch/blob/master/packages/backend/lib/query/visual/time-range.ts#L156
export const convertIntervalToMinutes = (interval: JourneyInterval): number => {
  const { unit, quantity } = interval;
  if (unit == null || quantity == null) {
    throw new Error("Invalid interval provided");
  }

  switch (unit) {
    case IntervalUnit.Minute:
      return quantity;
    case IntervalUnit.Hour:
      return quantity * MINUTES_IN_HOUR;
    case IntervalUnit.Day:
      return quantity * MINUTES_IN_DAY;
    case IntervalUnit.Week:
      return quantity * MINUTES_IN_WEEK;
    case IntervalUnit.Month:
      return quantity * MINUTES_IN_MONTH;
    case IntervalUnit.Year:
      return quantity * MINUTES_IN_YEAR;
    default:
      throw new UnreachableCaseError(unit);
  }
};

/**
 * isIntervalLessThan compares two intervals by converting to minutes and checking
 * if interval1 < interval2. The function assumes the input params have been validated.
 */
export const isIntervalLessThan = (
  interval1: JourneyInterval,
  interval2: JourneyInterval,
): boolean =>
  convertIntervalToMinutes(interval1) < convertIntervalToMinutes(interval2);

// convertTimeToMinutes expects a 24-hr (23:59) based time string and converts the input into minutes
const convertTimeToMinutes = (timeStr: string): number => {
  const [hours, minutes] = timeStr.split(":").map(Number);
  return (hours || 0) * MINUTES_IN_HOUR + (minutes || 0);
};

const daysOfWeek = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

/**
 * Finds the minimum gap in minutes for a VisualCRON expression over a week (including rollover [Sat->Sun]).
 * This function is written for journeys with the expectation that the feature only supports
 * Interval and VisualCRON (custom) schedules. Additionally, findMinimumGapInMinutesAcrossVisualCronSchedule
 * is only called after the ScheduleManager is used for setting/validating the schedule.
 *
 * @param schedule - a Custom (Visual CRON) schedule object
 * @returns number of minutes for the minimum found gap. Default will be 1 week.
 */
export const findMinimumGapInMinutesAcrossVisualCronSchedule = (
  schedule: Schedule,
): number => {
  if (!schedule || schedule.type !== ScheduleType.CUSTOM) {
    throw new Error("invalid schedule provided");
  }

  const scheduleExpressions = schedule.schedule?.expressions ?? [];
  const minutesInWeek: number[] = [];

  // Parse scheduleExpressions and then map times into minutes across a week
  for (const entry of scheduleExpressions) {
    for (const day in entry.days) {
      if (entry.days[day]) {
        // Map the given schedule expression into minutes across the entire week with
        // Sunday 12:00AM as minute 0 and Saturday 11:59PM as minute 10079.
        const dayIndex = daysOfWeek.indexOf(day);
        const timeMinutesAtDay = convertTimeToMinutes(entry.time);
        const calculatedMinutes = dayIndex * MINUTES_IN_DAY + timeMinutesAtDay;

        // Ensure calculatedMinutes is non-negative before pushing
        if (calculatedMinutes >= 0) {
          minutesInWeek.push(calculatedMinutes);
        }
      }
    }
  }

  // One week in minutes
  let minDuration = MINUTES_IN_WEEK;
  if (minutesInWeek.length <= 1) {
    return minDuration;
  }

  // Sort times based on the day of the week and time
  minutesInWeek.sort((a, b) => a - b);
  // Calculate the minimum gap between consecutive schedule times
  for (let i = 1; i < minutesInWeek.length; i++) {
    const currentTime = minutesInWeek[i];
    const previousTime = minutesInWeek[i - 1];

    if (currentTime == null || previousTime == null) {
      // Needed for typechecking
      continue;
    }

    minDuration = Math.min(minDuration, currentTime - previousTime);
  }

  const lastTime = minutesInWeek[minutesInWeek.length - 1];
  const firstTime = minutesInWeek[0];
  if (firstTime != null && lastTime != null) {
    // Calculate the difference between the first and last elements
    const wraparoundGap = MINUTES_IN_WEEK - lastTime + firstTime;
    minDuration = Math.min(minDuration, wraparoundGap);
  }

  return minDuration;
};

export function isParentModelOptionDisabled(
  connectionInformation:
    | {
        plan_in_warehouse: boolean;
        definition: { supportedQueries: string[]; type: string };
      }
    | null
    | undefined,
  flags: {
    enableRedshiftJourneys: boolean;
    enableSynapseJourneys: boolean;
    enableFabricJourneys: boolean;
    enableTrinoJourneys: boolean;
  },
) {
  if (!connectionInformation) {
    return true;
  }

  const isLightningSyncEnabled = connectionInformation.plan_in_warehouse;
  const supportedQueriesIncludesJourneyNode =
    connectionInformation.definition.supportedQueries.includes(
      QueryType.JourneyNode,
    );
  const isRedshift = connectionInformation.definition?.type === "redshift";
  const isSynapse = connectionInformation.definition?.type === "synapse";
  const isFabric = connectionInformation.definition?.type === "fabric";
  const isTrino = connectionInformation.definition?.type === "trino";

  return (
    !isLightningSyncEnabled ||
    !supportedQueriesIncludesJourneyNode ||
    (isRedshift && !flags.enableRedshiftJourneys) ||
    (isSynapse && !flags.enableSynapseJourneys) ||
    (isFabric && !flags.enableFabricJourneys) ||
    (isTrino && !flags.enableTrinoJourneys)
  );
}

export const formatToLocaleDateString = (inputDate: string) =>
  new Date(inputDate).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
  });

export const getFullSyncConfigFromSync = (
  sync: JourneySyncQuery["syncs"][number] | undefined,
): FullSyncConfig | undefined => {
  if (!sync) {
    return undefined;
  }

  return {
    slug: sync.slug ?? undefined,
    segment: {
      id: sync.segment?.id ?? "",
      name: sync.segment?.name ?? "",
    },
    destination: {
      id: sync.destination?.id ?? "",
      name: sync.destination?.name ?? null,
      type: sync.destination?.type ?? "",
      definition: {
        name: sync.destination?.definition?.name ?? null,
        icon: sync.destination?.definition?.icon ?? "",
        type: sync.destination?.definition?.type ?? "",
      },
    },
    schedule: sync.schedule,
    config: sync.config,
    sync_template_id: sync.sync_template_id
      ? String(sync.sync_template_id)
      : undefined,
    sync_template_overrides: sync.sync_template_overrides,
    draft: false,
  };
};

// Always unflatten, regardless of the `unflattenSyncTemplateConfigs` flag:
// the flag-off merge treats dotted override keys as literal, materializing
// the stale template value at the nested path — permanently, since the
// migrator skips overrides once sync_template_id is null.
export const getDetachedFullSyncConfig = (opts: {
  fullSyncConfig: FullSyncConfig;
  syncTemplate: { config?: Record<string, unknown> | null };
}): FullSyncConfig => {
  const { fullSyncConfig, syncTemplate } = opts;

  const mergedConfig = mergeSyncConfigWithTemplateAndOverrides({
    syncConfig:
      (fullSyncConfig.config as Record<string, unknown> | null) ?? null,
    syncTemplate: { config: syncTemplate.config ?? undefined },
    syncTemplateOverrides: fullSyncConfig.sync_template_overrides ?? undefined,
    unflattenDotNotationKeys: true,
  });

  return {
    ...fullSyncConfig,
    config: mergedConfig,
    sync_template_id: null,
    sync_template_overrides: null,
  };
};

// This deliberately duplicates the optional schema utilities in @hightouch/formkit.
// The app uses Yup 0.28.5 while formkit uses Yup 0.32.11, whose schema types are
// incompatible. Consolidate these copies if the app aligns its Yup version.
// Yup utility wrapper that lets one remove the .required() aspect of a schema. This function
// evaluates recursively.
const isYupLazy = (schema: any): schema is Yup.Lazy =>
  schema &&
  (schema.type === undefined || schema.type === "lazy") &&
  typeof schema._resolve === "function";
const isYupObject = (schema: any): schema is Yup.ObjectSchema<any> =>
  schema?.type === "object";
const isYupArray = (schema: any): schema is Yup.ArraySchema<any> =>
  schema?.type === "array";

const makeOptionalLazyField = (field: Yup.Lazy): Yup.Lazy =>
  Yup.lazy((value) => {
    const schema = (field as any)._resolve(value);
    return makeSchemaOptional(schema);
  });

const makeOptionalField = (field: any): Yup.Schema<any> => {
  if (isYupLazy(field)) {
    return makeOptionalLazyField(field);
  }
  if (isYupObject(field)) {
    return makeSchemaOptional(field);
  }

  if (isYupArray(field)) {
    const innerType = field.innerType || (field as any)._subType;
    let optionalInner: Yup.Schema<any> | undefined;

    if (innerType) {
      if (isYupLazy(innerType)) {
        optionalInner = makeOptionalLazyField(innerType);
      } else {
        optionalInner = makeOptionalField(innerType);
      }
    }

    const arraySchema = optionalInner
      ? Yup.array().of(optionalInner)
      : Yup.array();

    return arraySchema.notRequired().nullable().optional();
  }

  // For any field, if it has a clone method, create a completely optional version
  if (field && typeof field.clone === "function") {
    // Create a new field of the same type that's completely optional
    const baseType = field.type;
    let newField: any;

    if (baseType === "number") {
      newField = Yup.number().nullable().optional();
    } else if (baseType === "string") {
      newField = Yup.string().nullable().optional();
    } else if (baseType === "boolean") {
      newField = Yup.boolean().nullable().optional();
    } else if (baseType === "date") {
      newField = Yup.date().nullable().optional();
    } else {
      // Fallback: create a generic schema that accepts anything
      newField = Yup.mixed().nullable().optional();
    }

    return newField;
  }

  return typeof field.notRequired === "function"
    ? field.notRequired().nullable().optional()
    : field;
};

// Exported function that allows a yup schema to ignore its 'required' field.
export function makeSchemaOptional(schema: any): any {
  if (!schema) return schema;
  if (isYupLazy(schema)) {
    return makeOptionalLazyField(schema);
  }

  if (isYupObject(schema)) {
    const newShape: Record<string, any> = {};
    for (const key in schema.fields) {
      // biome-ignore lint/suspicious/noPrototypeBuiltins: TODO: fix
      if (Object.prototype.hasOwnProperty.call(schema.fields, key)) {
        newShape[key] = makeOptionalField(schema.fields[key]);
      }
    }
    return Yup.object(newShape).notRequired().nullable().optional();
  }

  // Handle arrays at the top level
  if (isYupArray(schema)) {
    return makeOptionalField(schema);
  }

  // Handle other schema types
  if (schema && typeof schema.optional === "function") {
    return schema.optional().nullable();
  }

  if (schema && typeof schema.notRequired === "function") {
    return schema.notRequired().nullable().optional();
  }

  return schema;
}

// Helper function to infer filterJourneyBy from selected node
export const getFilterJourneyByFromNode = (
  selectedNode: JourneyNode | undefined,
  entryNode: JourneyNode | undefined,
): JourneyEventType => {
  if (!selectedNode) {
    return "entered-journey";
  }

  // If this is the entry node, use "entered-journey"
  if (entryNode && selectedNode.id === entryNode.id) {
    return "entered-journey";
  }

  // For other nodes, default to "entered-tile"
  return "entered-tile";
};

/**
 * Collapses columns that resolve to the same field so the mapper never lists one
 * twice. A journey sync segment can surface the same entry-event column from two
 * independent sources: the backend already folds entry-event columns into the
 * segment's own `syncable_columns` (via `listSyncableColumns`), and
 * `journeyColumns` fetches them again client-side. Keeps the first occurrence.
 */
const dedupeSyncableColumns = (columns: SyncableColumn[]): SyncableColumn[] => {
  const seen = new Set<string>();
  return columns.filter((column) => {
    const key = JSON.stringify({
      alias: column.alias ?? null,
      name: column.name,
      reference: column.column_reference,
    });
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

/**
 * Resolves the `syncable_columns` for a journey sync's field picker.
 *
 * For cohort-entry journeys the base is the entry audience's columns (which
 * carry the audience's trait enrichments); otherwise it falls back to the
 * model's own columns. Event/context columns are passed in separately because
 * the syncable-columns resolver can't tell which journey's event columns to
 * return from a parent model alone. The combined list is de-duplicated because
 * the base can already include the journey's entry-event columns.
 */
export const mergeJourneySyncModel = <T>(
  model: (T & { syncable_columns: SyncableColumn[] }) | undefined | null,
  entryAudienceSyncableColumns?: SyncableColumn[] | null,
  journeyColumns?: SyncableColumn[] | null,
): T | undefined | null => {
  if (!model) {
    return model;
  }
  const base = entryAudienceSyncableColumns?.length
    ? entryAudienceSyncableColumns
    : model.syncable_columns;
  return {
    ...model,
    syncable_columns: dedupeSyncableColumns([
      ...base,
      ...(journeyColumns ?? []),
    ]),
  };
};

/**
 * Stable `select` for the entry audience's `SyncableColumns` query. Declared at
 * module scope so its reference never changes between renders (React Query
 * re-runs `select` and returns a new value whenever the function identity
 * changes). Returns `undefined` when the segment is missing so
 * `mergeJourneySyncModel` falls back to the model's own columns.
 */
export const selectEntryAudienceSyncableColumns = (
  data: SyncableColumnsQuery,
): SyncableColumn[] | undefined => data.segments_by_pk?.syncable_columns;
